import { logger } from '@automata/utils';
import { Action } from '@automata/wfb-api';
import { useCallback, useMemo, useState } from 'react';
import { useUnmount } from 'usehooks-ts';

import { TaskFields } from './components/TaskFields';
import { SidebarWrap } from './SidebarWrap';
import { getDriverArguments, getInstrument } from './utils';
import { deselectNodesEdges } from '../../features/graph/graphSlice';
import {
  selectCommentsById,
  selectDriverArgumentsByTaskProperties,
  selectDriverForTaskByTaskId,
  selectInstrumentDataById,
  selectInstrumentsForDropdown,
  selectParameterDefinitions,
  selectTaskPropertiesForm,
  selectTaskPropertiesStrict,
} from '../../features/workflow/selectors';
import {
  removeTaskParameter,
  setComment,
  updateTaskParameters,
  updateTaskProperties,
} from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  PropertiesType,
  type TaskProperties,
  type TaskPropertiesForm,
  type UpdateTaskParametersFn,
} from '../../types';
import {
  castTaskParameters,
  getTaskTimeEstimateTypeAndValue,
  isStartOrStopShakeTask,
  taskPropertiesToForm,
} from '../../utils';
import { createTaskFormPatch } from '../../utils/taskProperties';

/**
 * Use this instead of dispatching updateTaskProperties directly,
 * so that the dispatched action fully describes the change including related fields.
 */
export function useUpdateTaskProperties(
  id: string
): (formUpdate: Partial<TaskPropertiesForm>) => void {
  const dispatch = useAppDispatch();
  const prevProperties = useAppSelector(selectTaskPropertiesStrict)[id];

  if (
    prevProperties === undefined ||
    prevProperties.propertiesType !== PropertiesType.Action
  ) {
    if (prevProperties === undefined) {
      logger.error(
        `in useUpdateTaskProperties, id "${id}" does not have properties and cannot be updated`
      );
    } else {
      logger.error(
        `in useUpdateTaskProperties, expected "${id}" to be an action task. It cannot be updated.`
      );
    }
    return () => {
      logger.error(`cannot update properties for action task ${id}`);
    };
  }

  return (formUpdate) => {
    const propertiesPatch = createTaskFormPatch(formUpdate, prevProperties);
    dispatch(
      updateTaskProperties({
        ...propertiesPatch,
        id,
        propertiesType: PropertiesType.Action,
      })
    );
  };
}

export function useUpdateTaskParameters({
  id,
  driverArguments,
  driverAction,
}: {
  id: string;
  driverArguments: ReturnType<typeof getDriverArguments>;
  driverAction: Action | undefined;
}): UpdateTaskParametersFn {
  const dispatch = useAppDispatch();
  return useCallback(
    (parameterName: string, update: string): void => {
      if (driverArguments !== null) {
        const updates = castTaskParameters({
          driverArguments,
          parameters: { [parameterName]: update },
        });

        if (Object.keys(updates).length === 0) {
          dispatch(removeTaskParameter({ id, fieldName: parameterName }));
        } else {
          dispatch(
            updateTaskParameters({
              id,
              updates,
              isDataOutput: Boolean(
                Object.values(driverAction?.data_outputs ?? {}).find(
                  (output) =>
                    output.suffix_with_field?.field_name === parameterName
                )
              ),
            })
          );
        }
      } else {
        logger.error(
          'tried to dispatch updateTaskParameters with no driver',
          id
        );
      }
    },
    [dispatch, driverArguments, id, driverAction?.data_outputs]
  );
}

interface TaskSidebarProps {
  selectedNodeId: string;
  properties: TaskProperties;
}

export const TaskSidebar = ({
  selectedNodeId,
  properties: taskProperties,
}: TaskSidebarProps) => {
  // TODO(IL, 2024-04-05): DRY, don't repeat parts in
  // both ConnectedTaskPropertiesSidebar and ConnectedLabwarePropertiesSidebar
  const dispatch = useAppDispatch();
  // Pending changes are submitted on blur, or when this component unmounts.
  const [pendingChanges, setPendingChanges] = useState<
    Partial<TaskPropertiesForm>
  >({});

  const comment = useAppSelector(selectCommentsById)[selectedNodeId];
  const taskPropertiesForm = useAppSelector(selectTaskPropertiesForm)[
    selectedNodeId
  ];
  const instrumentsById = useAppSelector(selectInstrumentDataById);
  const driver = useAppSelector(selectDriverForTaskByTaskId)[selectedNodeId];
  const driverArguments = useAppSelector(
    selectDriverArgumentsByTaskProperties(taskProperties)
  );
  const parameterDefinitions = useAppSelector(selectParameterDefinitions);
  const taskType = taskProperties?.taskType;
  const instrumentsForDropdown = useAppSelector(
    selectInstrumentsForDropdown(taskType)
  );
  const taskParameters = taskProperties?.taskParameters;
  const { instrumentId } = taskProperties;

  const driverAction =
    driver !== undefined && taskType !== undefined
      ? driver.actions[taskType]
      : undefined;

  const instrument = getInstrument(instrumentId, instrumentsById);

  const showTimeEstimateField = useMemo(() => {
    // isSpinSpecialCase is a special case for the Hettich SBS300 centrifuge spin task.
    const isSpinSpecialCase =
      taskType === 'spin' &&
      (instrumentId === 'hettich_sbs300_centrifuge' ||
        instrumentId === 'centrifuge_1');
    // "Estimated time" doesn't make sense for these actions.
    const isShakeSpecialCase = isStartOrStopShakeTask(taskType);

    if (
      driver === undefined ||
      driverAction === undefined ||
      taskType === undefined ||
      isSpinSpecialCase ||
      isShakeSpecialCase
    ) {
      return false;
    }
    const { timeEstimateType } = getTaskTimeEstimateTypeAndValue({
      action: driverAction,
      parameters: taskParameters ?? {},
      userTimeEstimate: null,
      parameterDefinitions,
    });
    return timeEstimateType === 'userEstimated';
  }, [
    driver,
    driverAction,
    instrumentId,
    parameterDefinitions,
    taskParameters,
    taskType,
  ]);

  const handleSetComment = useCallback(
    (comment: string) => {
      dispatch(setComment({ id: selectedNodeId, comment }));
    },
    [dispatch, selectedNodeId]
  );

  const commitChanges = useUpdateTaskProperties(selectedNodeId);

  const updateTaskParameters = useUpdateTaskParameters({
    id: selectedNodeId,
    driverArguments,
    driverAction,
  });

  const updateTaskProperties = useUpdateTaskProperties(selectedNodeId);

  // can include override for immediate submit, rather than submit on blur.
  // this is useful for checkboxes and other "immediate" inputs.
  const handleSubmitForm = useCallback(
    (override?: Partial<TaskPropertiesForm>) => {
      const allChanges = { ...pendingChanges, ...override };

      if (Object.keys(allChanges).length > 0) {
        commitChanges(allChanges);
        setPendingChanges({});
      }
    },
    [commitChanges, pendingChanges]
  );

  useUnmount(() => handleSubmitForm());

  const handlePendingChange = useCallback(
    (update: Partial<TaskPropertiesForm>) => {
      setPendingChanges((prev) => ({ ...prev, ...update }));
    },
    []
  );

  const handleCloseSidebar = useCallback(() => {
    dispatch(deselectNodesEdges());
  }, [dispatch]);

  /** pendingValues are the committed properties, with any pending changes applied. */
  const pendingValues = useMemo((): TaskPropertiesForm | undefined => {
    if (taskProperties === undefined) return undefined;

    return {
      ...taskPropertiesToForm(taskProperties),
      ...pendingChanges,
    };
  }, [taskProperties, pendingChanges]);

  if (
    taskProperties === undefined ||
    taskPropertiesForm === undefined ||
    pendingValues === undefined
  ) {
    logger.error(`no task properties (or form) for node ${selectedNodeId}`);
    return null;
  }

  // HACK(IL, 2024-04-04): using 'key' to ensure that semicontrolled fields don't
  // do a late update on blur/unmount causing zombie form updates (see WBP-125)
  return (
    <SidebarWrap
      key={selectedNodeId}
      comment={comment}
      handleCloseSidebar={handleCloseSidebar}
      handleSetComment={handleSetComment}
    >
      <TaskFields
        instrument={instrument}
        instruments={instrumentsForDropdown}
        values={pendingValues}
        selectedInstrumentDriverArgumentsForTask={driverArguments}
        showTimeEstimateField={showTimeEstimateField}
        taskProperties={taskProperties}
        handlePendingChange={handlePendingChange}
        handleSubmitForm={handleSubmitForm}
        updateTaskParameters={updateTaskParameters}
        updateTaskProperties={updateTaskProperties}
      />
    </SidebarWrap>
  );
};
